import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useSeatConstraint} from "./rules/useSeatConstraint";
import {SeatConfig} from "../config/SeatConfig";
import {useSeatValidator} from "./rules/useSeatValidator";
import {AisleMode, useRoomConfig} from "./RoomConfigContext";
import {useUserConfigContext} from "./UserConfigContext";

export const SeatConfigContext = createContext({});

export default function SeatConfigProvider({children}) {

    let defaultNumRows = 1;
    let defaultSeat = SeatConfig.cinemove;
    let defaultNumSeats = 3;
    let defaultPreferredRow = 0;
    let defaultDistance = 375;
    let defaultModelId = 0;
    let defaultSeatType = "chair";

    const { userConfig } = useUserConfigContext();

    if (userConfig) {
        const config = userConfig;

        defaultNumRows = config.numRows;
        defaultNumSeats = config.numSeats;
        defaultPreferredRow = config.preferredRow;
        defaultDistance = config.distance;

        if (config.seatId === "roma") {
            defaultSeat = SeatConfig.roma;
            defaultSeatType = "seat";
        }
        else if (config.seatId === "odeon") {
            defaultSeat = SeatConfig.odeon;
            defaultSeatType = "seat";
        }
        else if (config.seatId === "selena") {
            defaultSeat = SeatConfig.selena;
            defaultSeatType = "seat";
        }
        else if (config.seatId === "cinehomedeluxe") {
            defaultSeat = SeatConfig.cinehomedeluxe;
            defaultSeatType = "chair";
        }

        defaultModelId = config.modelID;
    }

    const [config, setConfig] = useState(defaultSeat);
    const [modelID, setModelID] = useState(defaultModelId);
    const [showOccupants, setShowOccupants] = useState(false);
    const [seatType, setSeatType] = useState(defaultSeatType);

    const [numRows, setNumRows] = useState(defaultNumRows);
    const [numSeats, setNumSeats] = useState(defaultNumSeats);
    const [preferredRow, setPreferredRow] = useState(defaultPreferredRow);
    const {freeWidth, aisleMode, aisleWidth, setAisleWidth} = useRoomConfig();

    // distance between first chair and screen
    const [distance, setDistance] = useSeatConstraint(defaultDistance, numRows, config);

    useEffect(() => {
        setModelID(0);
    }, [config]);

    const gltf = useMemo(() => config.models[modelID]?.gltf, [config, modelID]);
    const occupantGltf = useMemo(() => config.occupant, [config]);

    // distance between preferred seat and screen (derived value)
    const primarySeatDistance = useMemo(() => {
        return config.rowDepth * preferredRow + distance;
    }, [config.rowDepth, preferredRow, distance]);

    const numAisles = aisleMode === AisleMode.Both? 2 : 1;
    const podiumPadding = 10;
    const seatWidth = useMemo(
        () => config.size[0] * numSeats + config.armrestWidth,
        [config, numSeats]
    );

    const maxAisleWidth = useMemo(
        () => (freeWidth - seatWidth - 2 * podiumPadding) / 2
    , [freeWidth, seatWidth, podiumPadding]);

    const podiumWidth = useMemo(() => {
        return seatWidth + podiumPadding * 2 + aisleWidth * numAisles;
    }, [seatWidth, aisleWidth, numAisles]);

    useEffect(() => { setAisleWidth(Math.min(aisleWidth, maxAisleWidth)); }, [aisleWidth, setAisleWidth, maxAisleWidth]);

    useEffect(() => {

        if (seatType === "chair" && config !== SeatConfig.cinemove && config !== SeatConfig.cinehomedeluxe) {
            setConfig(SeatConfig.cinemove);
        }
        else if (seatType === "seat" && (config === SeatConfig.cinemove || config === SeatConfig.cinehomedeluxe)) {
            setConfig(SeatConfig.roma);
        }
    }, [config, seatType, setConfig]);

    const podiumOffset = useMemo(() => {
        if (aisleMode === AisleMode.Both)
            return 0;

        if (aisleMode === AisleMode.Left)
            return aisleWidth * .5;

        return -aisleWidth * .5;
    }, [aisleWidth, aisleMode]);

    const setConfigID = useCallback(id => setConfig(SeatConfig[id]), []);
    const { score, errorCodes } = useSeatValidator(numRows, numSeats, distance, primarySeatDistance, config);

    const primaryEarPosition = useMemo(() => {
        return [0, config.rowHeight * preferredRow + 120, -primarySeatDistance + 20];
    }, [config.rowHeight, preferredRow, primarySeatDistance]);

    return (
        <SeatConfigContext.Provider value={{
            numRows, setNumRows,
            numSeats, setNumSeats,
            preferredRow, setPreferredRow,
            distance, setDistance,
            seatType, setSeatType,
            primarySeatDistance, primaryEarPosition,
            ...config, setConfigID,
            modelID, setModelID,
            score, errorCodes,
            podiumWidth, podiumOffset,
            showOccupants, setShowOccupants,
            maxAisleWidth,
            gltf, occupantGltf,
        }}>
            {children}
        </SeatConfigContext.Provider>
    );
}

export const useSeatConfig = () => useContext(SeatConfigContext);
